import Download from '../../assets/icons/download.svg';
import { BlobProvider } from "@react-pdf/renderer";
import { PDFDocument } from 'pdf-lib';
import Logo from '../../assets/images/watermark.png';
import Toast from '../toastify/Index';
import PDFGenerator from './PDFGenerator';
import { usePDFTableConfig } from '../../hooks/usePDFTableConfig';
import CommonButton from '../button/Button';
import { useTranslation } from 'react-i18next';

const GeneratePDF = ({ data = [], orientation = '', excludedKeys = [], customLabels = {}, customRowsPerPage = null, customColumnOrder = [], formatCell }) => {
    const { t } = useTranslation();
    const { columns, pages, rowsPerPage } = usePDFTableConfig({
        data,
        customLabels,
        excludedKeys,
        orientation,
        customColumnOrder,
        customRowsPerPage: customRowsPerPage,
    });

    const getImageDataUrl = async (imagePath) => {
        const response = await fetch(imagePath);
        const blob = await response.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    };

    const addImageWatermarkToPdf = async (pdfBytes, imageUrl) => {
        const pdfDoc = await PDFDocument.load(pdfBytes);
        const pages = pdfDoc.getPages();

        // Load image as data URL
        const imageDataUrl = await getImageDataUrl(imageUrl);
        const imageBytes = await fetch(imageDataUrl).then(res => res.arrayBuffer());

        // Try both PNG and JPG embedding
        let image;
        try {
            image = await pdfDoc.embedPng(imageBytes);
        } catch (e) {
            try {
                image = await pdfDoc.embedJpg(imageBytes);
            } catch (e) {
                Toast(`Image must be PNG or JPG`, 'error');
            }
        }

        for (const page of pages) {
            const { width, height } = page.getSize();

            const scale = 0.4;
            const imageWidth = image.width * scale;
            const imageHeight = image.height * scale;

            // Center the watermark
            const x = width / 2 - imageWidth / 2;
            const y = height / 2 - imageHeight / 2;

            page.drawImage(image, {
                x,
                y,
                width: imageWidth,
                height: imageHeight,
                opacity: 0.3,
            });
        }

        return await pdfDoc.save();
    };

    const applyWatermarkToBlobUrl = async (blobUrl) => {
        try {
            const response = await fetch(blobUrl);
            if (!response.ok) throw new Error("Failed to fetch PDF blob");
            const pdfBytes = await response.arrayBuffer();

            const watermarkedPdfBytes = await addImageWatermarkToPdf(pdfBytes, Logo);
            const blob = new Blob([watermarkedPdfBytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);

            // For testing, download automatically
            const a = document.createElement('a');
            a.href = url;
            a.download = 'report.pdf';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Also keep the window.open if needed
            window.open(url, '_blank');
        } catch (error) {
            Toast(`Failed to generate PDF with watermark: ${error.message}`, 'error')
        }
    };

    return (
        <div>
            <BlobProvider document={
                <PDFGenerator
                    data={data}
                    columns={columns}
                    orientation={orientation}
                    rowsPerPage={rowsPerPage}
                    formatCell={formatCell}
                />
            }
            >
                {({ url, blob }) => (
                    <a
                        href={url}
                        target="_blank"
                        onClick={(e) => {
                            e.preventDefault();
                            if (!data || data.length === 0) {
                                Toast("No data available to generate the PDF!", 'error');
                                return;
                            }
                            applyWatermarkToBlobUrl(url);
                        }}
                        rel="noopener noreferrer"
                    >
                        <CommonButton
                            text={t('PDF_Herunterladen')}
                            color="#fff"
                            className="add-konten-btn button-border-radius"
                            icon={<img src={Download} alt="Download Icon" className="pdf-generate-icon" />}
                        />
                    </a>
                )}
            </BlobProvider>
        </div>
    )
}

export default GeneratePDF;
